import { useQuery, type DocumentNode } from '@apollo/client'
import { Clear, NotInterested } from '@mui/icons-material'
import {
    Autocomplete,
    type AutocompleteProps,
    type AutocompleteRenderGetTagProps,
    Box,
    Chip,
    TextField,
    Typography,
} from '@mui/material'
import { useState, type FC } from 'react'
import { FilterWrapper } from 'recruiter/components/FilterWrapper'
import {
    minimalInputSx,
    minimalTagSx,
    excludeTagSx,
    toggleChipSx,
    filterChipSx,
} from './minimalFilterStyles'

export { FilterWrapper }

const tagIconSx = {
    fontSize: '0.95rem',
    cursor: 'pointer',
    borderRadius: '50%',
    padding: '2px',
    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
}

export const renderMinimalTags =
    <T,>(
        getLabel: (option: T) => string,
        isExcluded?: (option: T) => boolean,
        onToggleExclude?: (option: T) => void
    ) =>
    (value: T[], getTagProps: AutocompleteRenderGetTagProps) =>
        value.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5 }}>
                {value.map((option, index) => {
                    const label = getLabel(option)
                    if (!label) return null
                    const { key, onDelete, ...tagProps } = getTagProps({
                        index,
                    })
                    const excluded = isExcluded?.(option) ?? false
                    return (
                        <Typography
                            key={key}
                            {...tagProps}
                            onClick={onToggleExclude ? undefined : onDelete}
                            component="span"
                            sx={
                                excluded
                                    ? excludeTagSx
                                    : minimalTagSx(!!onToggleExclude)
                            }
                        >
                            {label}
                            {onToggleExclude && (
                                <NotInterested
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        onToggleExclude(option)
                                    }}
                                    sx={tagIconSx}
                                />
                            )}
                            <Clear
                                onClick={
                                    onToggleExclude
                                        ? (e) => {
                                              e.stopPropagation()
                                              onDelete(e)
                                          }
                                        : undefined
                                }
                                sx={tagIconSx}
                            />
                        </Typography>
                    )
                })}
            </Box>
        ) : null

type FilterAutocompleteProps<T> = Omit<
    AutocompleteProps<T, true, false, false>,
    | 'renderInput'
    | 'renderTags'
    | 'multiple'
    | 'size'
    | 'open'
    | 'onOpen'
    | 'onClose'
> & {
    getTagLabel: (option: T) => string
    isExcluded?: (option: T) => boolean
    onToggleExclude?: (option: T) => void
    onSearchChange?: (value: string) => void
}

export function FilterAutocomplete<T>({
    getTagLabel,
    isExcluded,
    onToggleExclude,
    onSearchChange,
    ...props
}: FilterAutocompleteProps<T>) {
    const [open, setOpen] = useState(false)

    return (
        <Autocomplete
            multiple
            size="small"
            open={open}
            onOpen={() => setOpen(true)}
            onClose={(_, reason) => {
                if (reason !== 'selectOption' && reason !== 'removeOption') {
                    setOpen(false)
                }
            }}
            onInputChange={
                onSearchChange ? (_, value) => onSearchChange(value) : undefined
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder="Type to search..."
                    sx={minimalInputSx}
                />
            )}
            renderTags={renderMinimalTags(
                getTagLabel,
                isExcluded,
                onToggleExclude
            )}
            renderOption={({ key, ...optionProps }, option, { selected }) => (
                <Box
                    component="li"
                    key={key}
                    {...optionProps}
                    sx={{
                        fontSize: '0.75rem',
                        py: 0.5,
                        minHeight: 'unset',
                        bgcolor: selected ? 'primary.50' : 'inherit',
                        borderLeft: selected ? '3px solid' : 'none',
                        borderLeftColor: 'primary.main',
                    }}
                >
                    {props.getOptionLabel?.(option) ?? String(option)}
                </Box>
            )}
            {...props}
        />
    )
}

interface StringFilterProps {
    label: string
    query: DocumentNode
    extractOptions: (data: any) => string[]
    value: string[]
    onChange: (values: string[]) => void
}

export const StringFilter: FC<StringFilterProps> = ({
    label,
    query,
    extractOptions,
    value,
    onChange,
}) => {
    const { data, loading } = useQuery(query)
    const options = extractOptions(data)

    return (
        <FilterWrapper label={label}>
            <FilterAutocomplete
                loading={loading}
                options={options}
                value={value}
                getOptionLabel={(o) => o}
                getTagLabel={(o) => o}
                getOptionKey={(o) => o}
                onChange={(_, newVal) => onChange(newVal)}
            />
        </FilterWrapper>
    )
}

interface IdFilterProps {
    label: string
    query: DocumentNode
    extractOptions: (data: any) => Array<{ id: string; value: string }>
    value: string[]
    onChange: (ids: string[]) => void
}

export const IdFilter: FC<IdFilterProps> = ({
    label,
    query,
    extractOptions,
    value,
    onChange,
}) => {
    const { data, loading } = useQuery(query)
    const options = extractOptions(data)
    const selected = options.filter((o) => value.includes(o.id))

    return (
        <FilterWrapper label={label}>
            <FilterAutocomplete
                loading={loading}
                options={options}
                value={selected}
                getOptionLabel={(o) => o.value}
                getTagLabel={(o) => o.value}
                getOptionKey={(o) => o.id}
                onChange={(_, newVal) => onChange(newVal.map((o) => o.id))}
            />
        </FilterWrapper>
    )
}

type OptionWithExcluded = { id: string; value: string; excluded: boolean }

interface ExcludeIdFilterProps {
    label: string
    query: DocumentNode
    extractOptions: (data: any) => Array<{ id: string; value: string }>
    value: string[]
    valueNot: string[]
    onChange: (include: string[], exclude: string[]) => void
}

export const ExcludeIdFilter: FC<ExcludeIdFilterProps> = ({
    label,
    query,
    extractOptions,
    value,
    valueNot,
    onChange,
}) => {
    const { data, loading } = useQuery(query)
    const rawOptions = extractOptions(data)

    const options: OptionWithExcluded[] = rawOptions.map((o) => ({
        ...o,
        excluded: false,
    }))

    const allSelected: OptionWithExcluded[] = [
        ...value
            .map((id) => {
                const opt = rawOptions.find((o) => o.id === id)
                return opt ? { ...opt, excluded: false } : null
            })
            .filter((x): x is OptionWithExcluded => x !== null),
        ...valueNot
            .map((id) => {
                const opt = rawOptions.find((o) => o.id === id)
                return opt ? { ...opt, excluded: true } : null
            })
            .filter((x): x is OptionWithExcluded => x !== null),
    ]

    const handleTagClick = (opt: OptionWithExcluded) => {
        if (!opt.excluded) {
            onChange(
                value.filter((v) => v !== opt.id),
                [...valueNot, opt.id]
            )
        } else {
            onChange(
                [...value, opt.id],
                valueNot.filter((v) => v !== opt.id)
            )
        }
    }

    return (
        <FilterWrapper label={label}>
            <FilterAutocomplete
                loading={loading}
                options={options}
                value={allSelected}
                getOptionLabel={(o) => o.value}
                getTagLabel={(o) => o.value}
                isExcluded={(o) => o.excluded}
                onToggleExclude={handleTagClick}
                getOptionKey={(o) => o.id}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                onChange={(_, newVal) => {
                    const include = newVal
                        .filter((v) => !v.excluded)
                        .map((v) => v.id)
                    const exclude = newVal
                        .filter((v) => v.excluded)
                        .map((v) => v.id)
                    onChange(include, exclude)
                }}
            />
        </FilterWrapper>
    )
}

interface IdChipFilterProps {
    label: string
    query: DocumentNode
    extractOptions: (data: any) => Array<{ id: string; value: string }>
    value: string[]
    onChange: (ids: string[]) => void
}

export const IdChipFilter: FC<IdChipFilterProps> = ({
    label,
    query,
    extractOptions,
    value,
    onChange,
}) => {
    const { data } = useQuery(query)
    const options = extractOptions(data)

    const toggle = (id: string) => {
        onChange(
            value.includes(id) ? value.filter((v) => v !== id) : [...value, id]
        )
    }

    return (
        <FilterWrapper label={label}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {options.map((opt) => (
                    <Chip
                        key={opt.id}
                        label={opt.value}
                        size="small"
                        onClick={() => toggle(opt.id)}
                        sx={toggleChipSx(value.includes(opt.id))}
                    />
                ))}
            </Box>
        </FilterWrapper>
    )
}

interface ExcludeIdChipFilterProps {
    label: string
    query: DocumentNode
    extractOptions: (data: any) => Array<{ id: string; value: string }>
    value: string[]
    valueNot: string[]
    onChange: (include: string[], exclude: string[]) => void
}

export const ExcludeIdChipFilter: FC<ExcludeIdChipFilterProps> = ({
    label,
    query,
    extractOptions,
    value,
    valueNot,
    onChange,
}) => {
    const { data } = useQuery(query)
    const options = extractOptions(data)

    const chipState = (id: string): 'unselected' | 'include' | 'exclude' => {
        if (value.includes(id)) return 'include'
        if (valueNot.includes(id)) return 'exclude'
        return 'unselected'
    }

    const handleSelect = (id: string) => {
        onChange([...value, id], valueNot)
    }

    const handleToggle = (id: string) => {
        if (value.includes(id)) {
            onChange(
                value.filter((v) => v !== id),
                [...valueNot, id]
            )
        } else {
            onChange(
                [...value, id],
                valueNot.filter((v) => v !== id)
            )
        }
    }

    const handleRemove = (id: string) => {
        onChange(
            value.filter((v) => v !== id),
            valueNot.filter((v) => v !== id)
        )
    }

    return (
        <FilterWrapper label={label}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {options.map((opt) => {
                    const state = chipState(opt.id)
                    const isActive = state !== 'unselected'
                    return (
                        <Chip
                            key={opt.id}
                            label={
                                isActive ? (
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                        }}
                                    >
                                        {opt.value}
                                        <NotInterested
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                handleToggle(opt.id)
                                            }}
                                            sx={tagIconSx}
                                        />
                                        <Clear
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                handleRemove(opt.id)
                                            }}
                                            sx={tagIconSx}
                                        />
                                    </Box>
                                ) : (
                                    opt.value
                                )
                            }
                            size="small"
                            onClick={
                                isActive
                                    ? undefined
                                    : () => handleSelect(opt.id)
                            }
                            sx={{
                                ...filterChipSx(state),
                                ...(isActive && {
                                    cursor: 'default',
                                    '&:hover': {},
                                }),
                            }}
                        />
                    )
                })}
            </Box>
        </FilterWrapper>
    )
}
